import React, { forwardRef, useState, Children, isValidElement, useRef, useMemo, cloneElement } from 'react';
import { createMemoStyles, removeUndefinedProperties, createSpacingStyles, clsx, visuallyHidden, defaultFonts, safeRest, usePreviousState, uniqueId, KEYS } from '@wds/utils';
import { List } from '@wds/list';
import { IconChevronUp, IconChevronDown } from '@wds/icons';
import PropTypes from 'prop-types';
import { useTheme } from 'react-jss';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest$2(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var _assign$1 = function __assign() {
  _assign$1 = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return _assign$1.apply(this, arguments);
};

function __rest$1(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

var useStyles$3 = createMemoStyles(function (_a) {
  var spacing = _a.spacing;
  return {
    wrenchBox: function wrenchBox(_a) {
      var _b, _c;

      var display = _a.display,
          flexProperties = _a.flexProperties,
          gridProperties = _a.gridProperties;

      var gap = gridProperties.gap,
          gridStyleProperties = __rest$1(gridProperties, ["gap"]);

      var _d = flexProperties.flexItemGrow,
          flexItemGrow = _d === void 0 ? undefined : _d,
          _e = flexProperties.flexWrap,
          flexWrap = _e === void 0 ? undefined : _e,
          flexStyleProperties = __rest$1(flexProperties, ["flexItemGrow", "flexWrap"]);

      return removeUndefinedProperties(_assign$1(_assign$1(_assign$1({}, gridStyleProperties), flexStyleProperties), {
        justifyContent: (_b = gridStyleProperties.justifyContent) !== null && _b !== void 0 ? _b : flexStyleProperties.justifyContent,
        alignItems: (_c = gridStyleProperties.alignItems) !== null && _c !== void 0 ? _c : flexStyleProperties.alignItems,
        flexGrow: flexItemGrow && 1,
        flexWrap: flexWrap && 'wrap',
        display: display !== null && display !== void 0 ? display : 'block',
        gap: gap ? spacing[gap] : null
      }));
    }
  };
});
/**
 * [IMPORTANT]
 * We have to call createSpacingStyles at the component level
 * Otherwise, the styles will be overridden by the global styles
 * This happens because JSS loads the styles in sequence
 */

var useSpacingStyles = createSpacingStyles();

var Box = function Box(props) {
  var containerRef = props.containerRef,
      className = props.className,
      children = props.children,
      _a = props.element,
      element = _a === void 0 ? 'div' : _a,
      display = props.display,
      alignItems = props.alignItems,
      justifyContent = props.justifyContent,
      flexDirection = props.flexDirection,
      flexItemGrow = props.flexItemGrow,
      flexJustify = props.flexJustify,
      flexAlign = props.flexAlign,
      flexWrap = props.flexWrap,
      gridTemplateColumns = props.gridTemplateColumns,
      gridTemplateRows = props.gridTemplateRows,
      gridAutoFlow = props.gridAutoFlow,
      placeItems = props.placeItems,
      gap = props.gap,
      m = props.m,
      mx = props.mx,
      my = props.my,
      mt = props.mt,
      mb = props.mb,
      ml = props.ml,
      mr = props.mr,
      p = props.p,
      px = props.px,
      py = props.py,
      pt = props.pt,
      pb = props.pb,
      pl = props.pl,
      pr = props.pr,
      rest = __rest$1(props, ["containerRef", "className", "children", "element", "display", "alignItems", "justifyContent", "flexDirection", "flexItemGrow", "flexJustify", "flexAlign", "flexWrap", "gridTemplateColumns", "gridTemplateRows", "gridAutoFlow", "placeItems", "gap", "m", "mx", "my", "mt", "mb", "ml", "mr", "p", "px", "py", "pt", "pb", "pl", "pr"]);

  var flexProperties = {
    alignItems: alignItems !== null && alignItems !== void 0 ? alignItems : flexAlign,
    justifyContent: justifyContent !== null && justifyContent !== void 0 ? justifyContent : flexJustify,
    flexDirection: flexDirection,
    flexItemGrow: flexItemGrow,
    flexWrap: flexWrap
  };
  var gridProperties = {
    justifyContent: justifyContent,
    alignItems: alignItems,
    gridTemplateColumns: gridTemplateColumns,
    gridTemplateRows: gridTemplateRows,
    gridAutoFlow: gridAutoFlow,
    placeItems: placeItems,
    gap: gap
  };
  var wrenchSpacing = useSpacingStyles({
    m: m,
    mx: mx,
    my: my,
    mt: mt,
    mb: mb,
    ml: ml,
    mr: mr,
    p: p,
    px: px,
    py: py,
    pt: pt,
    pb: pb,
    pl: pl,
    pr: pr
  }).wrenchSpacing;
  var wrenchBox = useStyles$3({
    flexProperties: flexProperties,
    gridProperties: gridProperties,
    display: display
  }).wrenchBox; // TODO: remove this type coercion when upgrading to React v18

  var Element = element;
  var classNames = clsx(className, wrenchBox, wrenchSpacing);
  return /*#__PURE__*/React.createElement(Element, _assign$1({
    className: classNames,
    ref: containerRef
  }, rest), children);
};

Box.defaultProps = {
  element: 'div',
  containerRef: undefined,
  display: undefined,
  children: null,
  className: undefined
};

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

var _excluded$1 = ["palette", "spacing"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var useStyles$1 = createMemoStyles(function (_ref) {
  var palette = _ref.palette,
      spacing = _ref.spacing,
      theme = _objectWithoutProperties(_ref, _excluded$1);

  var pane = {
    bgColor: palette.background.accent,
    padding: spacing[5]
  };
  return {
    wrenchPane: function wrenchPane(_ref2) {
      var hide = _ref2.hide,
          transparent = _ref2.transparent;
      return _objectSpread(_objectSpread(_objectSpread({}, hide && visuallyHidden), defaultFonts(theme)), {}, {
        background: transparent ? 'none' : pane.bgColor
      });
    },
    wrenchPaneBody: function wrenchPaneBody() {
      return {
        padding: pane.padding
      };
    }
  };
});
var useStyles$2 = useStyles$1;

var _excluded = ["children", "hide", "id", "padding", "labelledBy", "role", "transparent"];

var Pane = function Pane(_ref) {
  var children = _ref.children,
      hide = _ref.hide,
      id = _ref.id,
      padding = _ref.padding,
      labelledBy = _ref.labelledBy,
      role = _ref.role,
      transparent = _ref.transparent,
      rest = _objectWithoutProperties(_ref, _excluded);

  var theme = useTheme();

  var _useStyles = useStyles$2({
    hide: hide,
    transparent: transparent,
    theme: theme
  }),
      wrenchPane = _useStyles.wrenchPane,
      wrenchPaneBody = _useStyles.wrenchPaneBody;

  return /*#__PURE__*/React.createElement("div", Object.assign({
    className: wrenchPane,
    role: role,
    id: id,
    "aria-labelledby": labelledBy,
    "aria-hidden": hide
  }, safeRest(rest)), padding ? /*#__PURE__*/React.createElement("div", {
    className: wrenchPaneBody
  }, children) : children);
};

Pane.displayName = "Pane";
Pane.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  hide: PropTypes.bool,
  id: PropTypes.string,
  padding: PropTypes.bool,
  labelledBy: PropTypes.string,
  role: PropTypes.string,
  transparent: PropTypes.bool
};
Pane.defaultProps = {
  hide: false,
  id: undefined,
  padding: false,
  labelledBy: undefined,
  role: undefined,
  transparent: false
};
var Pane$1 = Pane;

function _typeof(obj) {
  "@babel/helpers - typeof";

  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function _typeof(obj) {
      return typeof obj;
    };
  } else {
    _typeof = function _typeof(obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

var SEMANTIC_COLORS = ['primary', 'secondary', 'success', 'warning', 'upgrade', 'error', 'info', 'muted'];
var LINK_COLORS = ['link'];
var TYPE_COLORS = ['dark', 'light', 'darkMuted', 'lightMuted', 'link'];
var BACKGROUND_COLORS = ['main', 'light', 'dark', 'overlay', 'accent', 'primary', 'success', 'warning', 'error', 'info', 'upgrade', 'muted'];
var BORDER_COLORS = ['main', 'active', 'hover', 'muted'];
var DATA_COLORS = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']; /// ///////////////////////// FONT TYPE START ////////////////////////////
var FONT_SIZES = ['caption', 'bodySm', 'body', 'cardTitle', 'sectionTitleSm', 'sectionTitle', 'pageTitle', 'hero2', 'hero1'];
var FONT_WEIGHTS = ['light', 'regular', 'medium'];

var dataTransform = function dataTransform(arr) {
  return Object.freeze(arr.map(function (x) {
    return "data-".concat(x);
  }));
};

var DATA_VIS_COLORS = dataTransform(DATA_COLORS);

var getDataColor = function getDataColor(s) {
  return s.split('-')[1];
}; /// ///////////////////////// HELPERS ////////////////////////////


var readOnlyIncludes = function readOnlyIncludes(arr, el) {
  return arr.includes(el);
}; /// ///////////////////////// TYPE GUARDS ////////////////////////////


var isColorShape = function isColorShape(c) {
  return _typeof(c) === 'object' && 'main' in c;
};

var isSemanticColor = function isSemanticColor(c) {
  return readOnlyIncludes(SEMANTIC_COLORS, c);
};

var isLinkColor = function isLinkColor(c) {
  return c === 'link';
};

var isTypeColor = function isTypeColor(c) {
  return readOnlyIncludes(TYPE_COLORS, c);
};

var isBackgroundColor = function isBackgroundColor(c) {
  return readOnlyIncludes(BACKGROUND_COLORS, c);
};

var isBorderColor = function isBorderColor(c) {
  return readOnlyIncludes(BORDER_COLORS, c);
};

var isDataColor = function isDataColor(c) {
  return readOnlyIncludes(DATA_COLORS, c);
};

var isDataVisColor = function isDataVisColor(c) {
  return readOnlyIncludes(DATA_VIS_COLORS, c);
};

var getColor = function getColor(palette, color, _a) {
  var _b = _a === void 0 ? {} : _a,
      _c = _b.semanticKey,
      semanticKey = _c === void 0 ? 'main' : _c,
      _d = _b.colorType,
      colorType = _d === void 0 ? 'semantic' : _d,
      fallback = _b.fallback;

  var c = fallback !== null && fallback !== void 0 ? fallback : 'currentcolor';
  if (!color) return c;
  var cType = colorType; // if passed a portion of the Pallette that is the semantic color use that.

  if (isColorShape(color)) c = color[semanticKey];else {
    // Check Semantic
    if (cType === 'semantic') {
      if (isSemanticColor(color)) c = palette[color][semanticKey];else cType = 'link';
    } // Check type and get Color


    if (cType === 'link') {
      if (isLinkColor(color)) c = palette[color][semanticKey];else cType = 'text';
    }

    if (cType === 'text') {
      if (isTypeColor(color)) c = palette[cType][color];else cType = 'background';
    }

    if (cType === 'background') {
      if (isBackgroundColor(color)) c = palette[cType][color];else cType = 'border';
    }

    if (cType === 'border') {
      if (isBorderColor(color)) c = palette[cType][color];else cType = 'data';
    }

    if (cType === 'data') {
      if (isDataVisColor(color)) c = palette[cType][getDataColor(color)];
      if (isDataColor(color)) c = palette[cType][color];
    }
  }
  return c;
};

process.env.NODE_ENV !== 'production' ? 'cdn.mtassets.net' : 'cdn.smassets.net';
var palette$1 = {
  transparent: 'transparent',
  primary: {
    main: '#00BF6F',
    contrast: '#FFFFFF'
  },
  secondary: {
    main: '#6B787F',
    contrast: '#FFFFFF'
  },
  success: {
    main: '#00BF6F',
    contrast: '#FFFFFF'
  },
  warning: {
    main: '#F05B24',
    contrast: '#FFFFFF'
  },
  error: {
    main: '#F05B24',
    contrast: '#FFFFFF'
  },
  info: {
    main: '#2DCCD3',
    contrast: '#333E48'
  },
  upgrade: {
    main: '#F9BE00',
    contrast: '#333E48'
  },
  muted: {
    main: '#D0D2D3',
    contrast: '#333E48'
  },
  link: {
    main: '#007FAA',
    contrast: '#FFFFFF'
  },
  text: {
    dark: '#333E48',
    light: '#FFFFFF',
    darkMuted: '#6B787F',
    lightMuted: '#D0D2D3',
    link: '#007FAA'
  },
  background: {
    main: '#F7F8FA',
    light: '#FFFFFF',
    dark: '#333E48',
    overlay: 'rgba(107, 120, 127, .86)',
    accent: '#EDEEEE',
    primary: '#E5F9F1',
    success: '#E5F9F1',
    warning: '#FDEBE5',
    error: '#F8ECF2',
    info: '#E6F9FA',
    upgrade: '#FEF2CC',
    muted: '#EDEEEE'
  },
  border: {
    main: '#D0D2D3',
    active: '#404040',
    hover: '#757575',
    muted: '#EDEEEE'
  },
  data: {
    one: '#507CB6',
    two: '#00BF6F',
    three: '#6BC8CD',
    four: '#F9BE00',
    five: '#7D5E90',
    six: '#DB4D5C',
    seven: '#768086',
    eight: '#D25F90',
    nine: '#A38364',
    ten: '#FF8B4F'
  }
};
({
  main: [1, 'solid', getColor(palette$1, 'main', {
    colorType: 'border'
  })],
  hover: [1, 'solid', getColor(palette$1, 'hover', {
    colorType: 'border'
  })],
  active: [1, 'solid', getColor(palette$1, 'active', {
    colorType: 'border'
  })],
  muted: [1, 'solid', getColor(palette$1, 'muted', {
    colorType: 'border'
  })],
  dark: [1, 'solid', getColor(palette$1, 'dark', {
    colorType: 'background'
  })],
  light: [2, 'solid', getColor(palette$1, 'light', {
    colorType: 'background'
  })]
});
process.env.NODE_ENV !== 'production' ? 'cdn.mtassets.net' : 'cdn.smassets.net';
var palette = {
  transparent: 'transparent',
  primary: {
    main: '#008323',
    contrast: '#FFFFFF'
  },
  secondary: {
    main: '#757575',
    contrast: '#FFFFFF'
  },
  success: {
    main: '#008323',
    contrast: '#FFFFFF'
  },
  warning: {
    main: '#F7B500',
    contrast: '#404040'
  },
  error: {
    main: '#CC0000',
    contrast: '#FFFFFF'
  },
  info: {
    main: '#2cb2f1',
    contrast: '#000000'
  },
  upgrade: {
    main: '#ffe11b',
    contrast: '#404040'
  },
  muted: {
    main: '#d0d0d0',
    contrast: '#404040'
  },
  link: {
    main: '#0a7cc1',
    contrast: '#FFFFFF'
  },
  text: {
    dark: '#404040',
    light: '#FFFFFF',
    darkMuted: '#757575',
    lightMuted: '#efefef',
    link: '#0a7cc1'
  },
  background: {
    main: '#f7f7f7',
    light: '#ffffff',
    dark: '#404040',
    overlay: 'rgba(117, 117, 117, .86)',
    accent: '#efefef',
    primary: '#E5F3E9',
    success: '#E5F3E9',
    warning: '#FFEEC0',
    error: '#FBEAEA',
    info: '#DCF0FF',
    upgrade: '#FFF9D1',
    muted: '#EFEFEF'
  },
  border: {
    main: '#d0d0d0',
    active: '#404040',
    hover: '#757575',
    muted: '#EDEEEE'
  },
  data: {
    one: '#507CB6',
    two: '#008323',
    three: '#6BC8CD',
    four: '#F9BE00',
    five: '#7D5E90',
    six: '#DB4D5C',
    seven: '#768086',
    eight: '#D25F90',
    nine: '#A38364',
    ten: '#FF8B4F'
  }
};
getColor(palette, 'main', {
  colorType: 'border'
});
({
  main: [1, 'solid', getColor(palette, 'main', {
    colorType: 'border'
  })],
  hover: [1, 'solid', getColor(palette, 'hover', {
    colorType: 'border'
  })],
  active: [1, 'solid', getColor(palette, 'active', {
    colorType: 'border'
  })],
  muted: [1, 'solid', getColor(palette, 'muted', {
    colorType: 'border'
  })],
  dark: [1, 'solid', getColor(palette, 'dark', {
    colorType: 'background'
  })],
  light: [2, 'solid', getColor(palette, 'light', {
    colorType: 'background'
  })]
});
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

createMemoStyles(function (_a) {
  var _b = _a.type,
      assets = _b.assets,
      fontFamily = _b.fontFamily,
      fontWeight = _b.fontWeight,
      fontSize = _b.fontSize;
  var fontPath = "".concat(assets.base).concat(assets.path);
  var fontFaces = FONT_WEIGHTS.map(function (name) {
    // we need this because the font file names are all National2-<Weight>.2.ext
    var upperCaseName = "".concat(name[0].toUpperCase()).concat(name.slice(1));
    var weight = fontWeight[name];
    return {
      fontFamily: assets.name,
      fontWeight: weight,
      fontDisplay: 'swap',
      src: "url(".concat(fontPath, "-").concat(upperCaseName, ".").concat(assets.version, ".woff2) format('woff2')"),
      fallbacks: [{
        src: "url(".concat(fontPath, "-").concat(upperCaseName, ".").concat(assets.version, ".woff) format('woff')")
      }, {
        src: "url(".concat(fontPath, "-").concat(upperCaseName, ".").concat(assets.version, ".eot#iefix) format('embedded-opentype')")
      }]
    };
  });
  return {
    '@font-face': fontFaces,
    '@global': {
      "[class*='wrench'], [class*='wrench']::before, [class*='wrench']::after": {
        fontFamily: fontFamily.base,
        '-webkit-font-smoothing': 'antialiased',
        fontSize: fontSize.body,
        boxSizing: 'border-box',
        padding: 0,
        margin: 0,
        listStyle: 'none'
      }
    }
  };
});

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return _assign.apply(this, arguments);
};

function __rest(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}

var isNotLink = function isNotLink(t) {
  return t !== 'link';
};

var hasFontWeight = function hasFontWeight(t) {
  return typeof t.fontWeight === 'string' || typeof t.fontWeight === 'number';
};

var useStyles = createMemoStyles(function (_a) {
  var _b = _a.type,
      fontFamily = _b.fontFamily,
      fontSize = _b.fontSize,
      fontWeight = _b.fontWeight,
      palette = _a.palette;
  return {
    wrenchTypography: function wrenchTypography(_a) {
      var align = _a.align,
          color = _a.color,
          variant = _a.variant,
          component = _a.component,
          weight = _a.weight;
      var chosenColor = getColor(palette, color, {
        fallback: palette.text.dark
      });
      var variantStyle = {};

      if (variant) {
        if (isNotLink(variant)) {
          variantStyle = {
            fontSize: fontSize[variant],
            fontWeight: {
              hero1: fontWeight.light,
              hero2: fontWeight.light,
              title: fontWeight.light,
              sectionTitle: fontWeight.light,
              sectionTitleSm: fontWeight.light,
              pageTitle: fontWeight.light,
              cardTitle: fontWeight.regular,
              body: fontWeight.regular,
              bodySm: fontWeight.regular,
              caption: fontWeight.regular
            }[variant]
          };
        } else {
          variantStyle = {
            color: palette.link.main,
            textDecoration: 'none',
            border: 'none',
            background: 'none',
            outline: 'none',
            cursor: 'pointer',
            fontWeight: 'inherit',
            fontSize: 'inherit',
            '&:hover, &:focus': {
              textDecoration: 'underline'
            }
          };
        }
      }

      var computedWeight = 'inherit';

      if (weight) {
        computedWeight = fontWeight[weight];
      } else if (component === 'strong') {
        computedWeight = fontWeight.medium;
      } else if (hasFontWeight(variantStyle)) {
        computedWeight = variantStyle.fontWeight;
      }

      return _assign(_assign(_assign({
        fontFamily: fontFamily.base,
        fontSize: fontSize.body,
        boxSizing: 'border-box',
        '-webkit-font-smoothing': 'antialiased',
        color: chosenColor,
        textAlign: align,
        lineHeight: 1.5,
        margin: 0,
        padding: 0
      }, variantStyle), variant && (variant === 'hero1' || variant === 'hero2') && {
        fontFamily: fontFamily === null || fontFamily === void 0 ? void 0 : fontFamily.headings
      }), {
        fontWeight: computedWeight
      });
    }
  };
});
var Typography = /*#__PURE__*/forwardRef(function (_a, ref) {
  var _b = _a.align,
      align = _b === void 0 ? 'left' : _b,
      _c = _a.color,
      color = _c === void 0 ? 'dark' : _c,
      _d = _a.component,
      component = _d === void 0 ? 'div' : _d,
      _e = _a.variant,
      variant = _e === void 0 ? 'body' : _e,
      weight = _a.weight,
      children = _a.children,
      rest = __rest(_a, ["align", "color", "component", "variant", "weight", "children"]);

  var wrenchTypography = useStyles({
    align: align,
    color: color,
    variant: variant,
    component: component,
    weight: weight
  }).wrenchTypography;
  var Component = component;
  return /*#__PURE__*/React.createElement(Component, _assign({
    className: wrenchTypography
  }, safeRest(rest), {
    ref: ref
  }), children);
});

__spreadArray(__spreadArray([], FONT_SIZES, true), ['link'], false);

__spreadArray(__spreadArray(__spreadArray([], SEMANTIC_COLORS, true), LINK_COLORS, true), TYPE_COLORS, true);

var AccordionItem = forwardRef(function (_a, ref) {
    var addOnLeft = _a.addOnLeft, addOnRight = _a.addOnRight, affordance = _a.affordance, ariaControls = _a.ariaControls, onKeyDown = _a.onKeyDown, children = _a.children, color = _a.color, disabled = _a.disabled, expanded = _a.expanded, id = _a.id, interactive = _a.interactive, marked = _a.marked, onClick = _a.onClick, paneComponent = _a.paneComponent, upgrade = _a.upgrade;
    var tabIndex = disabled ? '-1' : 0;
    return (React.createElement(List.Item, { accordion: true, addOnLeft: addOnLeft, addOnRight: addOnRight, affordance: affordance, "aria-controls": ariaControls, onKeyDown: onKeyDown, color: color, disabled: disabled, expanded: expanded, id: id, interactive: interactive, marked: marked, onClick: onClick, paneComponent: paneComponent, role: "button", upgrade: upgrade, tabIndex: tabIndex, ref: ref }, children));
});
AccordionItem.defaultProps = {
    addOnLeft: undefined,
    addOnRight: undefined,
    affordance: undefined,
    ariaControls: undefined,
    onKeyDown: undefined,
    children: undefined,
    color: undefined,
    disabled: false,
    expanded: false,
    id: undefined,
    interactive: false,
    marked: false,
    onClick: undefined,
    paneComponent: undefined,
    upgrade: false,
    subtitle: undefined,
    titleSize: 'md',
};
AccordionItem.displayName = 'AccordionItem';

var Accordion = function (_a) {
    var multiple = _a.multiple, interactive = _a.interactive, noRules = _a.noRules, dense = _a.dense, transparent = _a.transparent, onChange = _a.onChange, id = _a.id, children = _a.children, rest = __rest$2(_a, ["multiple", "interactive", "noRules", "dense", "transparent", "onChange", "id", "children"]);
    var _b = useState(Children.map(children, function (child, index) {
        return isValidElement(child) && child.props.expanded ? index : null;
    }).filter(function (val) { return val !== null; })), activeItems = _b[0], setActiveItems = _b[1];
    var prevActiveItems = usePreviousState(activeItems);
    var activeItemsHasChanged = prevActiveItems !== activeItems;
    var accListRef = useRef(null);
    var uniqueAccName = useMemo(function () { return uniqueId('wrench-acc-'); }, []);
    var uniqueAccPaneName = useMemo(function () { return uniqueId('wrench-accPane-'); }, []);
    /**
     * @param {number} ind
     * @return {void}
     */
    var handleOnChange = function (evt, index) {
        // Since dom node is a div, we can't check the disabled property
        if (evt.currentTarget !== null && evt.currentTarget.tabIndex > -1) {
            var newActiveItems = [];
            if (!multiple) {
                newActiveItems = activeItems[0] === index ? [] : [index];
            }
            else if (activeItems.includes(index)) {
                newActiveItems = activeItems.filter(function (item) { return item !== index; });
            }
            else {
                newActiveItems = activeItems.concat(index);
            }
            setActiveItems(newActiveItems);
            if (onChange) {
                var currentTargetIndex = newActiveItems[newActiveItems.length - 1];
                onChange(activeItems, currentTargetIndex);
            }
        }
    };
    /**
   * Accordion according to Accordion Design Pattern in WAI-ARIA Authoring Practices 1.1
   * see https://www.w3.org/TR/wai-aria-practices/examples/accordion/accordion.html
   *
   * Keyboard Support:
   *
  ```
  Space or Enter
    When focus is on the accordion header of a collapsed section, expands the section.
  Tab
    Moves focus to the next focusable element.
    All focusable elements in the accordion are included in the page Tab sequence.
  Shift + Tab
    Moves focus to the previous focusable element.
    All focusable elements in the accordion are included in the page Tab sequence.
  Down Arrow
    When focus is on an accordion header, moves focus to the next accordion header.
    When focus is on last accordion header, moves focus to first accordion header.
  Up Arrow
    When focus is on an accordion header, moves focus to the previous accordion header.
    When focus is on first accordion header, moves focus to last accordion header.
  Home
    When focus is on an accordion header, moves focus to the first accordion header.
  End
    When focus is on an accordion header, moves focus to the last accordion header.
  ```
   */
    var handleKeyDown = function (evt, ind) {
        if (evt.key === KEYS.SPACE || evt.key === KEYS.ENTER) {
            evt.preventDefault();
            handleOnChange(evt, ind);
            return;
        }
        var accList = accListRef.current
            ? Array.from(accListRef.current.children).map(function (acc) {
                return acc.firstChild;
            })
            : [];
        var accListLen = accList.length - 1;
        var newFocusTarget = null;
        switch (evt.key) {
            case KEYS.DOWN:
                evt.preventDefault();
                newFocusTarget = ind < accListLen ? accList[ind + 1] : accList[0];
                break;
            case KEYS.UP:
                evt.preventDefault();
                newFocusTarget = ind === 0 ? accList[accListLen] : accList[ind - 1];
                break;
            case KEYS.HOME:
                evt.preventDefault();
                newFocusTarget = accList[0];
                break;
            case KEYS.END:
                evt.preventDefault();
                newFocusTarget = accList[accListLen];
                break;
        }
        // Guard Null Case
        if (newFocusTarget !== null) {
            newFocusTarget.focus();
        }
    };
    /**
     * @param {node} paneChildren
     * @param {string} labelledBy
     * @param {string} paneId
     * @return {node}
     */
    var renderPane = function (paneChildren, labelledBy, paneId, expanded) {
        var _a;
        var padding = true;
        if (((_a = paneChildren.type) === null || _a === void 0 ? void 0 : _a.displayName) === 'List') {
            padding = false;
        }
        var paneProps = {
            padding: padding,
            labelledBy: labelledBy,
            hide: !expanded,
            role: 'region',
            id: paneId,
        };
        return (React.createElement(Pane$1, __assign({ transparent: true }, paneProps), paneChildren));
    };
    /**
     *
     * @param {node} child
     * @param {number} index
     * @param {boolean} expanded
     * @return {node}
     */
    var renderAccordionElement = function (child, index, expanded) {
        var _a, _b;
        var accordionName = (_a = child.props.id) !== null && _a !== void 0 ? _a : "".concat(uniqueAccName, "-").concat(index);
        var accordionPaneName = (_b = child.props.ariaControls) !== null && _b !== void 0 ? _b : "".concat(uniqueAccPaneName, "-").concat(index);
        var paneComponent = renderPane(child.props.children, accordionName, accordionPaneName, expanded);
        var titleVariant = child.props.titleSize === 'lg' ? 'cardTitle' : 'body';
        var childElements = (React.createElement(Box, { my: 4 },
            React.createElement(Box, { mb: 2 },
                React.createElement(Typography, { component: "strong", variant: titleVariant }, child.props.title)),
            child.props.subtitle && React.createElement(Typography, { variant: "bodySm", color: "secondary" }, child.props.subtitle)));
        return cloneElement(child, {
            children: childElements,
            expanded: expanded,
            key: accordionName,
            id: accordionName,
            // collapsed: !expanded,
            ariaControls: accordionPaneName,
            paneComponent: paneComponent,
            affordance: expanded ? (React.createElement(IconChevronUp, { title: "Chevron Up" })) : (React.createElement(IconChevronDown, { title: "Chevron Down" })),
            onClick: function (evt) { return handleOnChange(evt, index); },
            onKeyDown: function (evt) {
                return handleKeyDown(evt, index);
            },
            // multiple: multipleElement,
        });
    };
    var accordionList = Children.map(children, function (child, index) {
        var isExpandedItem = activeItems.includes(index);
        // check if a child prop changed and update state accordiongly
        var expandedProp = child ? child.props.expanded : null;
        var childExpandedChanged = isExpandedItem !== expandedProp;
        var onlyTheChildPropChanged = !activeItemsHasChanged && childExpandedChanged;
        if (onlyTheChildPropChanged) {
            setActiveItems(Children.map(children, function (c, i) {
                return isValidElement(c) && c.props.expanded ? i : null;
            }).filter(function (val) { return val !== null; }));
        }
        if (!isValidElement(child)) {
            return null;
        }
        return child.type.displayName === 'ListItem'
            ? child
            : renderAccordionElement(child, index, isExpandedItem);
    });
    return (React.createElement(List, __assign({ ref: accListRef, noRules: noRules, interactive: interactive, dense: dense, transparent: transparent, id: id }, safeRest(rest)), accordionList));
};
Accordion.defaultProps = {
    multiple: false,
    onChange: null,
    interactive: false,
    id: null,
    dense: false,
    transparent: false,
    noRules: false,
};
Accordion.Item = AccordionItem;

export { Accordion };
